/**
 * Devices REST API Service
 * Handles REST calls for device-related data
 */

export interface DeviceMetric {
  id: number;
  deviceId: number;
  createdAt: string;
  metrics: Record<string, number>;
  datasetId?: number;
}

class DevicesApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5150';
  }

  private getToken(): string {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.token || '';
    }
    return '';
  }

  private async request<T>(method: string, path: string, data?: any): Promise<T> {
    const token = this.getToken();

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${this.baseUrl}${path}`, options);
    const json = await response.json();

    if (!response.ok || json.status >= 400) {
      throw new Error(json.error || json.message || 'Request failed');
    }

    return json.data;
  }

  /**
   * Get device metrics with optional filtering
   * @param deviceId - Device ID to get metrics for
   * @param options - Optional filters (datasetId, from, to dates)
   */
  async getDeviceMetrics(
    deviceId: number,
    options?: {
      datasetId?: number;
      from?: Date;
      to?: Date;
    }
  ): Promise<DeviceMetric[]> {
    const params = new URLSearchParams();
    if (options?.datasetId) params.append('datasetId', String(options.datasetId));
    if (options?.from) params.append('from', options.from.toISOString());
    if (options?.to) params.append('to', options.to.toISOString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<DeviceMetric[]>('GET', `/api/devices/${deviceId}/metrics${query}`);
  }
}

export const devicesApi = new DevicesApi();
export default devicesApi;
